// /settings/account — согласия и сессии.
//
// Секции «Способы входа» здесь больше нет: вход в StudyHub один — единый профиль
// IEC ID, и отозвать его нельзя (docs/plans/IEC-ID-IMPLEMENTATION.md §8). Вместе
// с ней ушли `auth.getLoginMethods` / `auth.unlinkProvider` и возврат `?linked=`.
// См. docs/ui-spec/settings.account.{desktop,mobile}.md.

import { Button } from "@studyhub/ui";
import { toast } from "@studyhub/ui/toast";
import { IconCircleCheck, IconLogout, IconShieldLock } from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SettingsPageContainer, SettingsPageHeader } from "@/components/settings/page-header";
import { SettingsSection } from "@/components/settings/settings-shell";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";
import { useConfirm } from "@/hooks/use-confirm";
import { useAuthExpiredStore } from "@/lib/auth-expired-store";
import { performSignOut } from "@/lib/logout";
import { api } from "@/lib/trpc-client";

export const Route = createFileRoute("/settings/account")({
	component: AccountRoute,
	pendingComponent: PagePendingFallback,
});

function AccountRoute() {
	const { t, i18n } = useTranslation("settings");
	const navigate = useNavigate();
	const confirm = useConfirm();

	const consentsQuery = api.consents.history.useQuery(undefined, {
		staleTime: 60_000,
	});
	const logoutAllMutation = api.auth.logoutAll.useMutation();

	const dateFmt = useMemo(
		() => new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }),
		[i18n.language],
	);
	const consents = useMemo(() => {
		const rows = consentsQuery.data ?? [];
		return rows.map((c) => ({
			id: c.id,
			label: `${c.documentType} v${c.documentVersion}`,
			date: dateFmt.format(new Date(c.acceptedAt)),
		}));
	}, [consentsQuery.data, dateFmt]);

	function onLogout() {
		// Полный локальный cleanup через performSignOut (suppress «сессия истекла»).
		void performSignOut().finally(() => {
			toast.success(t("logoutSuccess"));
			void navigate({ to: "/" });
		});
	}

	async function onLogoutAll(): Promise<void> {
		const ok = await confirm({
			title: t("devices.revokeAllConfirmTitle"),
			description: t("devices.revokeAllConfirmBody"),
			confirmLabel: t("account.logoutAll"),
			tone: "destructive",
		});
		if (!ok) return;
		// Сначала revoke всех сессий на сервере, затем тот же локальный cleanup.
		useAuthExpiredStore.getState().suppress();
		logoutAllMutation.mutate(undefined, {
			onSettled: () => {
				void performSignOut().finally(() => {
					toast.success(t("logoutAllSuccess"));
					void navigate({ to: "/" });
				});
			},
		});
	}

	return (
		<SettingsPageContainer>
			<SettingsPageHeader
				title={t("account.title")}
				description={t("account.subtitle")}
				icon={<IconShieldLock stroke={1.75} />}
			/>

			<SettingsSection title={t("account.consents")} icon={<IconCircleCheck stroke={1.75} />}>
				{consentsQuery.isLoading ? (
					Array.from({ length: 3 }).map((_, i) => (
						<div key={i} className="px-4 py-3.5">
							<div className="h-4 w-48 animate-pulse rounded bg-muted" />
						</div>
					))
				) : consentsQuery.isError ? (
					<div className="px-4 py-3.5 text-sm text-destructive">
						{t("account.consentsLoadError")}
					</div>
				) : consents.length === 0 ? (
					<div className="px-4 py-3.5 text-sm text-muted-foreground">
						{t("account.consentsEmpty")}
					</div>
				) : (
					consents.map((c) => (
						<div
							key={c.id}
							className="flex items-center gap-2.5 px-4 py-3 text-sm transition-colors duration-fast hover:bg-[var(--surface-2)]"
						>
							<IconCircleCheck size={16} stroke={1.75} className="shrink-0 text-success" />
							<span className="flex-1 text-foreground">{c.label}</span>
							<span className="text-xs tabular-nums text-muted-foreground">{c.date}</span>
						</div>
					))
				)}
			</SettingsSection>

			<SettingsSection title={t("account.session")} icon={<IconLogout stroke={1.75} />}>
				{/* На mobile кнопки выхода — стопкой full-width с тач-таргетом ≥44px;
				    на sm+ — inline в ряд. */}
				<div className="flex flex-col gap-2 px-4 py-3.5 sm:flex-row sm:flex-wrap sm:items-center">
					<Button
						variant="outline"
						size="sm"
						className="w-full pointer-coarse:h-11 sm:w-auto"
						onClick={onLogout}
					>
						<IconLogout size={14} stroke={1.75} className="mr-1.5" />
						{t("account.logoutThis")}
					</Button>
					<Button
						variant="destructive"
						size="sm"
						className="w-full pointer-coarse:h-11 sm:w-auto"
						onClick={() => void onLogoutAll()}
					>
						{t("account.logoutAll")}
					</Button>
				</div>
			</SettingsSection>
		</SettingsPageContainer>
	);
}
